import React from "react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import AttachmentPreviewSkeleton from "./PreviewSkeleton";

type ImagePreviewProps = {
  fileUrl?: string;
  fileName?: string;
  zoomLevel?: number;
};

const ImagePreview: React.FC<ImagePreviewProps> = ({
  fileUrl,
  fileName,
  zoomLevel = 100,
}) => {
  const [isImageLoaded, setIsImageLoaded] = React.useState(false);
  const [hasLoadError, setHasLoadError] = React.useState(false);

  React.useEffect(() => {
    if (!fileUrl) {
      setIsImageLoaded(false);
      setHasLoadError(false);
      return;
    }

    setIsImageLoaded(false);
    setHasLoadError(false);

    const image = new Image();
    image.onload = () => setIsImageLoaded(true);
    image.onerror = () => setHasLoadError(true);
    image.src = fileUrl;
  }, [fileUrl]);

  if (!fileUrl) {
    return <AttachmentPreviewSkeleton zoomLevel={zoomLevel} />;
  }

  if (hasLoadError) {
    return <AttachmentPreviewSkeleton zoomLevel={zoomLevel} />;
  }

  const zoomScale = zoomLevel / 100;

  return (
    <PhotoProvider>
      <main className="h-full min-h-0 w-full bg-gray-100 overflow-auto">
        {!isImageLoaded ? (
          <AttachmentPreviewSkeleton zoomLevel={zoomLevel} />
        ) : (
          <div className="flex min-h-full w-full items-center justify-center p-6 box-border">
            <div
              className="flex justify-center"
              style={{ width: `${zoomLevel}%` }}
            >
              <PhotoView src={fileUrl}>
                <img
                  src={fileUrl}
                  alt={fileName || "Attachment image"}
                  className="h-auto w-full object-contain cursor-zoom-in"
                  style={{
                    maxHeight: `calc((100vh - 128px) * ${zoomScale})`,
                  }}
                />
              </PhotoView>
            </div>
          </div>
        )}
      </main>
    </PhotoProvider>
  );
};

export default ImagePreview;
